import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { PawIcon } from "@/components/Icons";
import { Menu, X, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { t, lang, setLang, langs } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#home", label: t.nav.home, id: "nav-home" },
    { href: "#services", label: t.nav.services, id: "nav-services" },
    { href: "#about", label: t.nav.about, id: "nav-about" },
    { href: "#gallery", label: t.nav.gallery, id: "nav-gallery" },
    { href: "#faq", label: t.nav.faq, id: "nav-faq" },
    { href: "#contact", label: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_4px_20px_rgba(107,63,42,0.06)]" : "bg-white/60 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
        <a
          href="#home"
          data-testid="navbar-logo"
          className="flex items-center gap-2 group"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f7c5c0] to-[#f4956a] flex items-center justify-center text-white shadow-md group-hover:rotate-12 transition-transform">
            <PawIcon size={22} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-[#6b3f2a] text-lg sm:text-xl">Carol</span>
            <span className="text-[10px] sm:text-xs font-semibold tracking-[0.15em] uppercase text-[#8a5a40]">
              Tierbetreuung
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="px-3 py-2 rounded-full text-[#6b3f2a] font-semibold hover:bg-[#fbe1de] transition-colors text-sm"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switcher"
                className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[#6b3f2a] hover:bg-[#fbe1de] transition-colors font-bold text-sm"
                aria-label="Language"
              >
                <Globe size={16} />
                {lang.toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl">
              {langs.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`cursor-pointer rounded-xl font-semibold ${
                    l.code === lang ? "bg-[#fbe1de] text-[#6b3f2a]" : ""
                  }`}
                >
                  {l.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a href="#contact" className="hidden sm:inline-flex btn-primary text-sm" data-testid="navbar-cta">
            <PawIcon size={16} /> {t.nav.cta}
          </a>

          <button
            className="lg:hidden p-2 rounded-full text-[#6b3f2a] hover:bg-[#fbe1de]"
            onClick={() => setOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#fbe1de] px-5 py-3 flex flex-col gap-1" data-testid="mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="px-3 py-3 rounded-2xl text-[#6b3f2a] font-semibold hover:bg-[#fbe1de]"
              data-testid={`mobile-${l.id}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="btn-primary mt-2 justify-center"
            data-testid="mobile-cta"
          >
            <PawIcon size={16} /> {t.nav.cta}
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
